import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Badge, Card, Select, Switch } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { dateTime, relativeTime } from '@/lib/format';
import { listPhoneCalls } from '@/data/client';
import type { PhoneCall } from '@/data/types';
import { useMe } from '@/auth/useMe';
import { usePhone } from '@/phone/PhoneProvider';
import { accountLink } from '@/phone/Softphone';

/* Phone → Calls: every call on Triton's numbers, who it was, who took it, how
   long, and whether the transcript landed. */
export function Phone() {
  const { me } = useMe();
  const phone = usePhone();
  const [mine, setMine] = useState(false);
  const [direction, setDirection] = useState<'' | 'inbound' | 'outbound'>('');
  const [missed, setMissed] = useState(false);
  const { data: calls = [], isLoading } = useQuery({
    queryKey: ['phoneCalls', { mine, direction, missed }], queryFn: () => listPhoneCalls({ mine, direction, missed }), refetchInterval: 30_000,
  });

  if (phone.status && !phone.status.configured) {
    return (
      <div className="dash-empty">
        <h2>Phone is not set up yet</h2>
        <p>Calls and texts from Triton's numbers will live here. {me?.isAdmin ? <Link to="/settings?tab=phone">Set up Twilio in Settings → Phone.</Link> : 'An admin sets it up in Settings → Phone.'}</p>
      </div>
    );
  }

  return (
    <div className="crm-list">
      <div className="lead-toolbar">
        <div className="lead-toolbar__filters">
          <label className="lead-field"><Icon name="users" size={16} />
            <Select aria-label="Whose calls" options={[{ value: 'all', label: 'Everyone' }, { value: 'me', label: 'Mine' }]} value={mine ? 'me' : 'all'} onChange={(e) => setMine(e.target.value === 'me')} /></label>
          <label className="lead-field"><Icon name="filter" size={16} />
            <Select aria-label="Direction" options={[{ value: '', label: 'In and out' }, { value: 'inbound', label: 'Inbound' }, { value: 'outbound', label: 'Outbound' }]} value={direction} onChange={(e) => setDirection(e.target.value as '' | 'inbound' | 'outbound')} /></label>
          <Switch label="Missed only" checked={missed} onChange={(e) => setMissed(e.target.checked)} />
        </div>
        <div className="lead-toolbar__right">
          {phone.status?.numbers.map((n) => <span key={n.id} className="crm-cell-sub">{n.label ?? n.purpose} {n.display}</span>)}
        </div>
      </div>
      <Card padding={false}>
        <table className="dash-table lead-table">
          <thead><tr><th>When</th><th>Who</th><th>Rep</th><th>Length</th><th>Status</th><th>Outcome</th><th>Transcript</th></tr></thead>
          <tbody>
            {calls.map((c) => <CallRow key={c.id} c={c} />)}
          </tbody>
        </table>
        {!isLoading && !calls.length && <p className="lead-none">No calls yet. {phone.enabled ? 'Set yourself Available in the top bar to take calls, or click a phone number on any account to call.' : ''}</p>}
      </Card>
    </div>
  );
}

const STATUS: Record<PhoneCall['status'], { label: string; variant: 'success' | 'warning' | 'neutral' | 'outline' }> = {
  ringing: { label: 'Ringing', variant: 'warning' }, in_progress: { label: 'On the call', variant: 'success' }, completed: { label: 'Answered', variant: 'neutral' },
  missed: { label: 'Missed', variant: 'warning' }, no_answer: { label: 'No answer', variant: 'outline' }, busy: { label: 'Busy', variant: 'outline' },
  failed: { label: 'Failed', variant: 'warning' }, canceled: { label: 'Cancelled', variant: 'outline' },
};

function CallRow({ c }: { c: PhoneCall }) {
  const link = accountLink(c);
  const s = STATUS[c.status];
  const mmss = c.durationSec != null ? `${Math.floor(c.durationSec / 60)}:${String(c.durationSec % 60).padStart(2, '0')}` : '—';
  return (
    <tr>
      <td title={dateTime(c.startedAt)}><span className="pq-nowrap">{relativeTime(c.startedAt)}</span><span className="crm-cell-sub">{dateTime(c.startedAt)}</span></td>
      <td>
        <span className="sp-dir"><Icon name={c.direction === 'inbound' ? 'arrow-down-right' : 'arrow-up-right'} size={14} /> {link ? <Link to={link}>{c.who ?? c.otherParty}</Link> : <strong>{c.who ?? c.otherParty}</strong>}</span>
        <span className="crm-cell-sub">{c.who ? c.otherParty : 'Not on any account'}{c.companyName && c.companyName !== c.who ? ` · ${c.companyName}` : ''}</span>
      </td>
      <td className="crm-cell-muted">{c.userName ?? '—'}</td>
      <td className="crm-cell-muted">{c.status === 'completed' ? mmss : '—'}</td>
      <td><Badge variant={s.variant}>{s.label}</Badge></td>
      <td className="crm-cell-muted">{c.outcome ?? '—'}</td>
      <td className="crm-cell-muted">
        {c.transcriptStatus === 'stored' && c.companyId ? <Link to={`/companies/${c.companyId}?tab=vault`}>In the Vault</Link>
          : c.transcriptStatus === 'stored' ? 'On the prospect' : c.transcriptStatus === 'inbox' ? <Link to="/settings?tab=integrations">In the inbox</Link>
            : c.transcriptStatus === 'running' ? 'Transcribing…' : c.transcriptStatus === 'error' ? 'Failed' : '—'}
      </td>
    </tr>
  );
}
